import { create } from 'zustand';

import { AUTO_QUIET_MS } from '@/hooks/useBulkJobs/constants';

/** Maximum number of completed jobs retained in the store per session. */
const MAX_RECENT_JOBS = 10;

export type BulkJobStatus = 'queued' | 'running' | 'pass' | 'fail' | 'partial';

export interface BulkJobProgress {
  processed: number;
  total: number;
  failed: number;
}

export interface BulkJobRecord {
  /** Server-assigned job key returned from POST /bulk/{resource}. */
  jobId: string;
  resource: string;
  action: string;
  /** Client-side wall-clock timestamp at mutate() call (used for auto-quiet). */
  submittedAt: number;
  /**
   * Client-side wall-clock timestamp stamped once when the job first reaches a
   * terminal status (pass/fail/partial). `null` while in-flight. The drawer
   * anchors a terminal row's frozen completion time to this instead of letting
   * the relative age climb from submittedAt (ENG-5184). No backend field is
   * consumed for this — consistent with the client-stamped submittedAt.
   */
  completedAt: number | null;
  /** Keys affected by this job. Empty Set for query-mode until resolved. */
  pendingKeys: Set<string>;
  status: BulkJobStatus;
  progress: BulkJobProgress | null;
  /** Raw job.comment string from the server poll. */
  comment: string | null;
  /**
   * When true this job is excluded from activeCount/unviewedCount.
   * Set by markSuppressedIfFast() when terminal status arrives within
   * AUTO_QUIET_MS of submission — prevents badge flicker for fast jobs.
   * UX divergence #3.
   */
  suppressed: boolean;
}

interface BulkJobsState {
  jobs: Map<string, BulkJobRecord>;
  /** Count of newly-terminal (non-suppressed) jobs not yet seen by the user. */
  unviewedCount: number;
  /** Union of all pendingKeys across active (queued/running) jobs. Rebuilt on every mutation for O(1) isKeyPending lookups. */
  allPendingKeys: Set<string>;

  // --- Mutations ---
  addJob: (
    jobId: string,
    meta: Pick<BulkJobRecord, 'resource' | 'action' | 'pendingKeys'>
  ) => void;
  setJobResolvedKeys: (jobId: string, keys: string[]) => void;
  updateJobStatus: (
    jobId: string,
    status: BulkJobStatus,
    progress: BulkJobProgress | null,
    comment: string | null
  ) => void;
  /**
   * Called when a terminal poll arrives within AUTO_QUIET_MS of submission.
   * Flips suppressed=true so the badge never shows this job.
   * UX divergence #3.
   */
  markSuppressedIfFast: (jobId: string) => void;
  removeJob: (jobId: string) => void;
  clearUnviewed: () => void;

  // --- Selectors ---
  isKeyPending: (key: string) => boolean;
  /** Number of non-suppressed in-flight jobs (queued or running). */
  activeCount: () => number;
  recentJobs: () => BulkJobRecord[];
}

const isTerminal = (status: BulkJobStatus): boolean =>
  status === 'pass' || status === 'fail' || status === 'partial';

/** Rebuild the set of all pending keys from the current jobs map. O(total keys). */
function buildAllPendingKeys(jobs: Map<string, BulkJobRecord>): Set<string> {
  const result = new Set<string>();
  for (const job of jobs.values()) {
    if (job.status === 'queued' || job.status === 'running') {
      for (const key of job.pendingKeys) {
        result.add(key);
      }
    }
  }
  return result;
}

export const useBulkJobsStore = create<BulkJobsState>((set, get) => ({
  jobs: new Map(),
  unviewedCount: 0,
  allPendingKeys: new Set<string>(),

  addJob: (jobId, { resource, action, pendingKeys }) => {
    set(state => {
      const next = new Map(state.jobs);
      next.set(jobId, {
        jobId,
        resource,
        action,
        submittedAt: Date.now(),
        completedAt: null,
        pendingKeys,
        status: 'queued',
        progress: null,
        comment: null,
        suppressed: false,
      });
      const allPendingKeys = buildAllPendingKeys(next);
      return { jobs: next, allPendingKeys };
    });
  },

  setJobResolvedKeys: (jobId, keys) => {
    set(state => {
      const job = state.jobs.get(jobId);
      if (!job) return state;
      const next = new Map(state.jobs);
      next.set(jobId, { ...job, pendingKeys: new Set(keys) });
      const allPendingKeys = buildAllPendingKeys(next);
      return { jobs: next, allPendingKeys };
    });
  },

  updateJobStatus: (jobId, status, progress, comment) => {
    set(state => {
      const job = state.jobs.get(jobId);
      if (!job) return state;
      const next = new Map(state.jobs);

      // Atomically apply suppression when a terminal status arrives within
      // AUTO_QUIET_MS of submission.  Checking here — before incrementing
      // unviewedCount — eliminates the race where callers would have to call
      // markSuppressedIfFast() BEFORE updateJobStatus() to prevent a spurious
      // badge increment.  Callers no longer need to order the two calls
      // correctly; markSuppressedIfFast() remains for backwards-compat but is
      // redundant for fast-job suppression when this path is taken.
      const becameTerminal = !isTerminal(job.status) && isTerminal(status);
      const elapsed = Date.now() - job.submittedAt;
      const autoSuppressed = becameTerminal && elapsed < AUTO_QUIET_MS;

      const updated = {
        ...job,
        status,
        progress,
        comment,
        // Stamp completion time exactly once, on the terminal transition.
        // Preserve any existing value so a later poll for the same terminal
        // job never re-stamps a climbing timestamp (ENG-5184).
        completedAt: becameTerminal ? Date.now() : job.completedAt,
        suppressed: job.suppressed || autoSuppressed,
      };
      next.set(jobId, updated);

      // Prune to MAX_RECENT_JOBS by removing oldest terminal jobs
      const allJobs = Array.from(next.values());
      const excessCount = allJobs.length - MAX_RECENT_JOBS;
      if (excessCount > 0) {
        const terminalJobs = allJobs
          .filter(j => isTerminal(j.status))
          .sort((a, b) => a.submittedAt - b.submittedAt);
        terminalJobs.slice(0, excessCount).forEach(j => next.delete(j.jobId));
      }

      // Increment unviewed count when a non-suppressed job becomes terminal.
      // Suppression is already folded into updated.suppressed above.
      const newUnviewed =
        becameTerminal && !updated.suppressed
          ? state.unviewedCount + 1
          : state.unviewedCount;

      const allPendingKeys = buildAllPendingKeys(next);
      return { jobs: next, unviewedCount: newUnviewed, allPendingKeys };
    });
  },

  markSuppressedIfFast: jobId => {
    // NOTE: updateJobStatus() now applies fast-job suppression atomically, so
    // callers that call markSuppressedIfFast() after updateJobStatus() will
    // find the job already suppressed and unviewedCount already correct.
    // This function is kept for backwards-compatibility with any callers that
    // invoke it independently (e.g. non-terminal status updates or edge cases).
    set(state => {
      const job = state.jobs.get(jobId);
      if (!job) return state;
      const elapsed = Date.now() - job.submittedAt;
      if (elapsed >= AUTO_QUIET_MS) return state;
      const next = new Map(state.jobs);
      next.set(jobId, { ...job, suppressed: true });
      const allPendingKeys = buildAllPendingKeys(next);
      return { jobs: next, allPendingKeys };
    });
  },

  removeJob: jobId => {
    set(state => {
      const next = new Map(state.jobs);
      next.delete(jobId);
      const allPendingKeys = buildAllPendingKeys(next);
      return { jobs: next, allPendingKeys };
    });
  },

  clearUnviewed: () => set({ unviewedCount: 0 }),

  isKeyPending: key => {
    return get().allPendingKeys.has(key);
  },

  activeCount: () => {
    const { jobs } = get();
    let count = 0;
    for (const job of jobs.values()) {
      if (
        !job.suppressed &&
        (job.status === 'queued' || job.status === 'running')
      ) {
        count++;
      }
    }
    return count;
  },

  recentJobs: () => {
    const { jobs } = get();
    return Array.from(jobs.values()).sort(
      (a, b) => b.submittedAt - a.submittedAt
    );
  },
}));
